// App.js
import React, { useContext, Suspense, lazy, useMemo } from "react";
import { Route, Routes } from "react-router-dom";
import AboutPage from "./Pages/AboutPage";
// import AuthPage from "./Pages/AuthPage.jsx";
import ContactPage from "./Pages/ContactPage";
import ErrorPage from "./Pages/ErrorPage";
import Home from "./Pages/Home";
import ServicesPage from "./Pages/ServicesPage";
import PortfolioPage from "./Pages/PortfolioPage";
import Layout from "./components/Layout";
import FaqPage from "./Pages/FaqPage";
import { DataContext } from "./context/DataContext";
import HomePage from "./Pages/auth/HomePage.jsx";
import Profile from "./Pages/auth/Profile";
import ChangePassword from "./Pages/auth/ChangePassword";
// import PasswordResetConfirm from "./components/Password/PasswordResetConfirm.jsx";
// import PasswordResetRequest from "./components/Password/PasswordResetRequest.jsx"
import BillingHistory from "./Pages/auth/BillingHistory";
import ManageSubscription from "./Pages/auth/ManageSubscription";
import AuthLayout from "./components/AuthLayout";
import PrivateRoute from "./PrivateRoute.js";
import PublicRoute from "./PublicRoute.js";
import LoginRedirect from "./components/LoginRedirect";
import PaymentMethodForm from "./components/PaymentMethod/PaymentMethodForm.jsx";
import TestCreditCard from "./components/CreditCards/TestCreditCard.jsx";
import ProtectedRoute from "./Route/ProtectedRoute.jsx";
import AuthOnlyProtectedRoute from "./Route/AuthOnlyProtectedRoute.jsx"
import Success from './components/Success/index.jsx';
import Cancel from './components/Cancel/index.jsx';
import ResendVerificationEmail from "./components/ResendVerificationEmail";
import EmailVerificationPage from "./Pages/EmailVerificationPage";
import PickPayoutModal from "./components/PickPayoutModal";
import OnboardingComplete from "./components/Onboarding/OnboardingComplete";
import OnboardingRefresh from "./components/Onboarding/OnboardingRefresh";
// import ReferralDashboardLazy from "./components/Referral/ReferralDashboard";

// Spinner for Suspense fallback (uses existing bootstrap styles)
import { Spinner } from "react-bootstrap";

// ─── Lazy-loaded heavy components ───────────────────────────────────────────
const DataTable = lazy(() => import("./Pages/auth/DataTable.jsx"));
const HotColdMatchups = lazy(() => import("./Pages/auth/HotColdMatchups"));
const ReferralDashboardLazy = lazy(() => import("./components/Referral/ReferralDashboard"));
const AuthPageLazy = lazy(() => import("./Pages/AuthPage.jsx"));
const PasswordResetConfirmLazy = lazy(() => import("./components/Password/PasswordResetConfirm.jsx"));
const PasswordResetRequestLazy = lazy(() => import("./components/Password/PasswordResetRequest.jsx"));

function App() {
  const data = useContext(DataContext) || {};
  
  // Debug logging for data in App component
  console.log('[App] DataContext keys:', Object.keys(data));
  console.log('[App] playerPropsNBA count:', data.playerPropsNBA ? data.playerPropsNBA.length : 'undefined');


  // Configurations for MLB - wrapped in useMemo to react to data changes
  const mlbConfigs = useMemo(() => ({
    opponentstrikeout: {
      displayName: "Team Strikeouts per Game",
      data: data.strikeoutsPerGame || [],
      titleText:
      "Displays the average total strikeouts per game by each team in the MLB",
    },
    opponenthit: {
      displayName: "Team Hits per Game",
      data: data.hitsPerGame || [],
      titleText:
      "Displays the average total hits per game by each team in the MLB",
    },
    HotBatterMatchups: {
      displayName: "Hot Batter Matchups",
      data: (data.baseballMatchupStats || []).filter(
        (r) => r.matchupStat === "hotbatterMatch"
      ),
      titleText:
        "Displays which hitters have a hot matchup vs todays opposing pitcher. Based on previous career matchups.",
    },
    ColdBatterMatchups: {
      displayName: "Cold Batter Matchups",
      data: (data.baseballMatchupStats || []).filter(
        (r) => r.matchupStat === "coldbatterMatch"
      ),
      titleText:
        "Displays which hitters have a cold matchup vs todays opposing pitcher. Based on previous career matchups.",
    },
    HotStreaks_MLB: {
      displayName: "HotStreaks MLB",
      data: data.hotStreaksMLB || [],
      titleText:
        "Collects and displays player props that are currently on streaks hitting their over/under consistently in their most recent games.",
    },
    innings: {
      displayName: "1st Inning Runs",
      data: data.iningsRuns || [],
      titleText:
      "Displays each teams average runs scored in the first inning at home and away. Specifically for the runs first inning betting prop.",
    },
    mlbProjections: {
      displayName: "MLB Projections",
      // Cap to the top 50 (BettingPros returns them ordered by edge, best first)
      data: (data.playerPropsMLB || []).slice(0, 50),
      titleText:
        "The MLB projections model uses a mathematical equation to predict the outcome of player props.",
    },
  }), [data]);

  // Configurations for NBA - wrapped in useMemo to react to data changes
  const nbaConfigs = useMemo(() => ({
    pointsPerGame: {
      displayName: "Opponent Points per Game",
      data: data.pointsPerGame || [],
      titleText:
      "Displays the average total points ALLOWED per game by each team in the NBA",
    },
    reboundsPerGame: {
      displayName: "Opponent Rebounds per Game",
      data: data.reboundsPerGame || [],
      titleText:
      "Displays the average total rebounds ALLOWED per game by each team in the NBA",
    },
    assistsPerGame: {
      displayName: "Opponent Assists per Game",
      data: data.assistsPerGame || [],
      titleText:
      "Displays the average total assist ALLOWED per game by each team in the NBA",
    },
    HotStreaks_NBA: {
      displayName: "HotStreaks NBA",
      data: data.hotStreaksNBA || [],
      titleText:
        "Collects and displays player props that are currently on streaks hitting their over/under consistently in their most recent games.",
    },
    nbaProjections: {
      displayName: "NBA Projections",
      data: data.playerPropsNBA || [],
      titleText:
        "",
    },
  }), [data]);

  // Configurations for NFL - wrapped in useMemo to react to data changes
  const nflConfigs = useMemo(() => ({
    Yard: {
      displayName: "HotStreaks NFL",
      data: data.hotStreaksNFL || [],
      titleText:
        "Collects and displays player props that are currently on streaks hitting their over/under consistently in their most recent games.",
    },
    RushingYards: {
      displayName: "Rushing Yards Allowed",
      data: data.rushingYardsAllowed || [],
      titleText: "Displays the average total rushing yards ALLOWED per game by each team in the NFL",
    },    
    PassingYards: {
      displayName: "Pass Yards Allowed",
      data: data.passYardsAllowed || [],
      titleText: "Displays the average total passing yards ALLOWED per game by each team in the NFL",
    },
    nflProjections: {
      displayName: "NFL Projections",
      data: data.playerPropsNFL || [],
      titleText:
        "",
    },
  }), [data]);

  // Configurations for +EV - wrapped in useMemo to react to data changes
  const evConfigs = useMemo(() => {
    // Split player stats by sport for better organization
    const allStats = data.playerStats || [];
    console.log('[EV CONFIG] Total playerStats received:', allStats.length);
    console.log('[EV CONFIG] Sample of playerStats:', allStats.slice(0, 5).map(s => `${s.PlayerName} - ${s.Category}`));
    
    // Categorize by sport based on Category field
    const nflStats = allStats.filter(stat => 
      ['Passing Yards', 'Rushing Yards', 'Receiving Yards', 'Receptions'].includes(stat.Category)
    );
    // The NBA runs late October through June. Out of season the ev scraper stops
    // collecting NBA props, and this keeps any leftover rows out of the table so
    // stale lines can't skew the model. Comes back on its own in October.
    const month = new Date().getMonth() + 1;
    const nbaInSeason = month >= 10 || month <= 6;
    const nbaStats = nbaInSeason
      ? allStats.filter(stat =>
          ['Points', 'Rebounds', 'Assists'].includes(stat.Category)
        )
      : [];
    const mlbStats = allStats.filter(stat => 
      ['Strikeouts', 'Hits Allowed'].includes(stat.Category)
    );
    
    console.log('[EV CONFIG] Filtered counts - NFL:', nflStats.length, 'NBA:', nbaStats.length, 'MLB:', mlbStats.length);
    console.log('[EV CONFIG] Sample NBA stats:', nbaStats.slice(0, 3).map(s => `${s.PlayerName} - ${s.Category}`));

    // Hide rows with DK odds -150 or worse (PrizePicks doesn't show these)
    const hasShowableOdds = (stat) => {
      const parsed = parseInt(stat.Draftkings, 10);
      if (isNaN(parsed)) return true;
      return parsed > -150;
    };

    // Combine all stats into one table, filtering out unshowable odds
    const allEvStats = [...nflStats, ...nbaStats, ...mlbStats].filter(hasShowableOdds);
    console.log('[EV CONFIG] Combined all stats (after -150 filter):', allEvStats.length);

    return {
      EV_ALL: {
        displayName: "+EV Model",
        data: allEvStats,
        titleText:
          `Live ${nbaInSeason ? "NFL, NBA, and MLB" : "NFL and MLB"} player props from upcoming games (updated every 5 hours). Shows DraftKings odds for player props to identify +EV opportunities on PrizePicks and Underdog.`,
      },
    };
  }, [data]);




  return (
    <Suspense
      fallback={
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "100vh" }}
        >
          <Spinner animation="border" variant="light" />
        </div>
      }
    >
      <Routes>
        {/* <Route path="/login-fake" element={<LoginRedirect />} /> */}
        {/* Public Routes */}
        <Route path="/" element={<PublicRoute><Layout /></PublicRoute>}>
          <Route index element={<Home />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="pricing" element={<ServicesPage />} />
          <Route path="portfolio" element={<PortfolioPage />} />
          <Route path="login" element={<AuthPageLazy />} />
          <Route path="register" element={<AuthPageLazy />} />
          <Route path="/password-reset" element={<PasswordResetRequestLazy />} />
          <Route path="/password-reset-confirm" element={<PasswordResetConfirmLazy />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="faq" element={<FaqPage />} />
          <Route path="/resend-verification" element={<ResendVerificationEmail />} />
          <Route path="/verify-email/:token" element={<EmailVerificationPage />} />
        </Route>

        {/* Auth-only route (for subscribe page) */}
        <Route element={<AuthOnlyProtectedRoute />}>
          <Route path="/subscribe" element={<ServicesPage />} />
          {/* <Route path="/checkout" element={<Checkout />} /> */}
          <Route path="/subscription-success" element={<Success />} />
          <Route path="/subscription-cancel" element={<Cancel />} />
          <Route path="/onboarding/complete" element={<OnboardingComplete />} />
          <Route path="/onboarding/refresh" element={<OnboardingRefresh />} />
        </Route>

        {/* Authenticated and Subscribed Routes */}
        <Route element={<ProtectedRoute />}>
          <Route path="/auth" element={<AuthLayout />}>
            {/* NBA Routes */}
            <Route
              path="nba"
              element={
                <DataTable
                  configs={nbaConfigs}
                />
              }
            />
            <Route
              path="nba/points-per-game"
              element={
                <DataTable
                  configs={{ pointsPerGame: nbaConfigs.pointsPerGame }}
                  colors={["#31df31", "yellow", "red"]}
                />
              }
            />
            <Route
              path="nba/rebounds-per-game"
              element={
                <DataTable
                  configs={{ reboundsPerGame: nbaConfigs.reboundsPerGame }}
                  colors={["#31df31", "yellow", "red"]}
                />
              }
            />
            <Route
              path="nba/assist-per-game"
              element={
                <DataTable
                  configs={{ assistsPerGame: nbaConfigs.assistsPerGame }}
                  colors={["#31df31", "yellow", "red"]}
                />
              }
            />
            <Route
              path="nba/hotstreaks-nba"
              element={
                <DataTable
                  configs={{ HotStreaks_NBA: nbaConfigs.HotStreaks_NBA }}
                />
              }
            />
            <Route
              path="nba/nba-projections"
              element={
                <DataTable
                  configs={{ nbaProjections: nbaConfigs.nbaProjections }}
                />
              }
            />

            {/* MLB Routes */}
            <Route
              path="mlb"
              element={
                <DataTable
                  configs={mlbConfigs}
                />
              }
            />
            <Route
              path="mlb/strikeouts"
              element={
                <DataTable
                  configs={{ opponentstrikeout: mlbConfigs.opponentstrikeout }}
                  colors={["#31df31", "yellow", "red"]}
                />
              }
            />
            <Route
              path="mlb/hits"
              element={
                <DataTable
                  configs={{ opponenthit: mlbConfigs.opponenthit }}
                  colors={["#31df31", "yellow", "red"]}
                />
              }
            />
            <Route
              path="mlb/hotstreaks-mlb"
              element={
                <DataTable
                  configs={{ HotStreaks_MLB: mlbConfigs.HotStreaks_MLB }}
                />
              }
            />
            <Route
              path="mlb/first-inning-runs"
              element={
                <DataTable
                  configs={{ innings: mlbConfigs.innings }}
                  colors={["#31df31", "yellow", "red"]}
                />
              }
            />
            <Route
              path="mlb/mlb-projections"
              element={
                <DataTable
                  configs={{ mlbProjections: mlbConfigs.mlbProjections }}
                />
              }
            />  
            <Route
              path="mlb/coldhot-matchup"
              element={<HotColdMatchups />}
            />

            {/* NFL Routes */}
            <Route
              path="nfl"
              element={
                <DataTable
                  configs={nflConfigs}
                />
              }
            />
            <Route
              path="nfl/hotstreaks-nfl"
              element={
                <DataTable
                  configs={{ Yard: nflConfigs.Yard }}
                />
              }
            />
            <Route
              path="nfl/rushing-yards-allowed"
              element={
                <DataTable
                  configs={{ RushingYards: nflConfigs.RushingYards }}
                  colors={["#31df31", "yellow", "red"]}
                />
              }
            />
            <Route
              path="nfl/pass-yards-allowed"
              element={
                <DataTable
                  configs={{ PassingYards: nflConfigs.PassingYards }}
                  colors={["#31df31", "yellow", "red"]}
                />
              }
            />
            <Route
              path="nfl/nfl-projections"
              element={
                <DataTable
                  configs={{ nflProjections: nflConfigs.nflProjections }}
                />
              }
            />

            {/* +EV Routes */}
            <Route
              path="ev"
              element={
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '1rem 2rem' }}>
                    <PickPayoutModal />
                  </div>
                  <DataTable configs={evConfigs} />
                </>
              }
            />
            <Route
              path="ev/ev"
              element={
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '1rem 2rem' }}>
                    <PickPayoutModal />
                  </div>
                  <DataTable configs={evConfigs} />
                </>
              }
            />

            {/* User Options */} 
            <Route index element={<HomePage />} />
            <Route path="profile" element={<Profile />} />
            <Route path="ChangePassword" element={<ChangePassword />} />
            <Route path="billing-history" element={<BillingHistory />} />
            <Route path="manage-subscription" element={<ManageSubscription />} />
            <Route path="payment-method" element={<PaymentMethodForm />} />
            <Route path="referrals" element={<ReferralDashboardLazy />} />
          </Route>
        </Route>
        {/* <Route path="/test-credit-card" element={<TestCreditCard />} /> */}
        {/* Fallback Route */}
        <Route path="*" element={<ErrorPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;
